import { parseColor, type OklchColor } from "@/document/model/color";
import {
  createGradientStop,
  documentPointToGradientPoint,
  normalizeGradientPaint,
  type GradientStop,
  type LinearGradientPaint,
  type NodeEffect,
  type Paint,
  type RadialGradientPaint,
} from "@/document/model/styles";
import { createId } from "@/lib/ids";
import type { Rect } from "@/lib/math/rect";
import type { SvgImportIssueCollector } from "./issues";
import { childElements, elementLocalName, readSvgLength } from "./parseSvgDocument";
import { imagePaintFromPatternElement, readSvgImageHref } from "./svgImages";

export class SvgDefsRegistry {
  private readonly linearGradients = new Map<string, Element>();
  private readonly radialGradients = new Map<string, Element>();
  private readonly patterns = new Map<string, Element>();
  private readonly images = new Map<string, Element>();
  private readonly elementsById = new Map<string, Element>();
  private readonly filters = new Map<string, Element>();
  private readonly clipPaths = new Map<string, Element>();
  private readonly masks = new Map<string, Element>();

  static fromDocument(root: Element): SvgDefsRegistry {
    const registry = new SvgDefsRegistry();
    registry.indexSubtree(root);
    return registry;
  }

  resolvePaint(reference: string | null | undefined, bounds: Rect, issues: SvgImportIssueCollector): Paint | undefined {
    const id = parseUrlReference(reference);
    if (!id) {
      return undefined;
    }

    const linear = this.linearGradients.get(id);
    if (linear) {
      return parseLinearGradientElement(linear, bounds, issues);
    }

    const radial = this.radialGradients.get(id);
    if (radial) {
      return parseRadialGradientElement(radial, bounds, issues);
    }

    const pattern = this.patterns.get(id);
    if (pattern) {
      return parsePatternElement(pattern, bounds, this, issues);
    }

    issues.addOnce("missing-reference", `Paint reference "#${id}" was not found`, id);
    return undefined;
  }

  getElementById(id: string): Element | undefined {
    return this.elementsById.get(id);
  }

  resolveClipPath(reference: string | null | undefined): Element | undefined {
    const id = parseUrlReference(reference);
    return id ? this.clipPaths.get(id) : undefined;
  }

  resolveMask(reference: string | null | undefined): Element | undefined {
    const id = parseUrlReference(reference);
    return id ? this.masks.get(id) : undefined;
  }

  resolveFilterEffects(reference: string | null | undefined, issues: SvgImportIssueCollector): NodeEffect[] {
    const id = parseUrlReference(reference);
    if (!id) {
      return [];
    }

    const filter = this.filters.get(id);
    if (!filter) {
      issues.addOnce("missing-reference", `Filter reference "#${id}" was not found`, id);
      return [];
    }

    return parseFilterElement(filter, issues);
  }

  private indexSubtree(element: Element): void {
    const localName = elementLocalName(element);
    const id = element.getAttribute("id");
    if (id) {
      this.elementsById.set(id, element);
      if (localName === "lineargradient") {
        this.linearGradients.set(id, element);
      } else if (localName === "radialgradient") {
        this.radialGradients.set(id, element);
      } else if (localName === "pattern") {
        this.patterns.set(id, element);
      } else if (localName === "image") {
        this.images.set(id, element);
      } else if (localName === "filter") {
        this.filters.set(id, element);
      } else if (localName === "clippath") {
        this.clipPaths.set(id, element);
      } else if (localName === "mask") {
        this.masks.set(id, element);
      }
    }

    for (const child of childElements(element)) {
      this.indexSubtree(child);
    }
  }
}

export function parseUrlReference(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  const match = trimmed.match(/^url\(#([^)]+)\)$/i);
  return match?.[1];
}

function parseLinearGradientElement(element: Element, bounds: Rect, issues: SvgImportIssueCollector): LinearGradientPaint {
  const units = element.getAttribute("gradientUnits") ?? "objectBoundingBox";
  const x1 = readGradientCoordinate(element.getAttribute("x1"), bounds, "x", units, 0);
  const y1 = readGradientCoordinate(element.getAttribute("y1"), bounds, "y", units, 0);
  const x2 = readGradientCoordinate(element.getAttribute("x2"), bounds, "x", units, 1);
  const y2 = readGradientCoordinate(element.getAttribute("y2"), bounds, "y", units, 0);
  const start = documentPointToGradientPoint(bounds, { x: x1, y: y1 });
  const end = documentPointToGradientPoint(bounds, { x: x2, y: y2 });
  const colorSpace = element.getAttribute("color-interpolation") === "linearRGB" ? "rgb" : "rgb";

  return normalizeGradientPaint({
    type: "linearGradient",
    angle: 0,
    start,
    end,
    colorSpace,
    stops: parseGradientStops(element, issues),
  });
}

function parseRadialGradientElement(element: Element, bounds: Rect, issues: SvgImportIssueCollector): RadialGradientPaint {
  const units = element.getAttribute("gradientUnits") ?? "objectBoundingBox";
  const cx = readGradientCoordinate(element.getAttribute("cx"), bounds, "x", units, 0.5);
  const cy = readGradientCoordinate(element.getAttribute("cy"), bounds, "y", units, 0.5);
  const r = readGradientLength(element.getAttribute("r"), bounds, units, 0.5);
  const center = units === "userSpaceOnUse"
    ? documentPointToGradientPoint(bounds, { x: cx, y: cy })
    : { x: cx, y: cy };
  const radius = units === "userSpaceOnUse"
    ? r / Math.max(Math.abs(bounds.width), Math.abs(bounds.height), 0.0001)
    : r;

  return normalizeGradientPaint({
    type: "radialGradient",
    center,
    radius,
    colorSpace: "rgb",
    stops: parseGradientStops(element, issues),
  });
}

function parseGradientStops(gradient: Element, issues: SvgImportIssueCollector): GradientStop[] {
  const stops = childElements(gradient)
    .filter(child => elementLocalName(child) === "stop")
    .map(stop => {
      const offset = parseStopOffset(stop.getAttribute("offset"));
      const color = parseStopColor(stop, issues);
      return createGradientStop(offset, color);
    })
    .sort((left, right) => left.position - right.position);

  if (stops.length === 0) {
    issues.addOnce("unsupported-gradient", "Gradient is missing color stops", elementLocalName(gradient));
    return [createGradientStop(0, "#000000"), createGradientStop(1, "#ffffff")];
  }

  return stops;
}

function parseStopOffset(value: string | null): number {
  if (!value) {
    return 0;
  }
  const trimmed = value.trim();
  if (trimmed.endsWith("%")) {
    return Number.parseFloat(trimmed) / 100;
  }
  return Number.parseFloat(trimmed);
}

function parseStopColor(stop: Element, issues: SvgImportIssueCollector): string | OklchColor {
  const inline = stop.getAttribute("style");
  const inlineColor = inline?.match(/stop-color:\s*([^;]+)/i)?.[1]?.trim();
  const raw = inlineColor ?? stop.getAttribute("stop-color") ?? "#000000";
  const opacityRaw = stop.getAttribute("stop-opacity")
    ?? inline?.match(/stop-opacity:\s*([^;]+)/i)?.[1]?.trim()
    ?? "1";
  try {
    const color = parseColor(raw);
    const opacity = readSvgLength(opacityRaw, 1);
    return { ...color, alpha: color.alpha * opacity };
  } catch {
    issues.addOnce("unsupported-attribute", `Could not parse gradient stop color "${raw}"`, "stop");
    return "#000000";
  }
}

function readGradientCoordinate(
  value: string | null,
  bounds: Rect,
  axis: "x" | "y",
  units: string,
  fallback: number,
): number {
  if (!value) {
    return units === "userSpaceOnUse"
      ? (axis === "x" ? bounds.x : bounds.y) + (axis === "x" ? bounds.width : bounds.height) * fallback
      : fallback;
  }

  const trimmed = value.trim();
  if (units === "userSpaceOnUse") {
    if (trimmed.endsWith("%")) {
      const ratio = Number.parseFloat(trimmed) / 100;
      return (axis === "x" ? bounds.x : bounds.y) + (axis === "x" ? bounds.width : bounds.height) * ratio;
    }
    return readSvgLength(trimmed, fallback);
  }

  if (trimmed.endsWith("%")) {
    return Number.parseFloat(trimmed) / 100;
  }

  return readSvgLength(trimmed, fallback);
}

function readGradientLength(
  value: string | null,
  bounds: Rect,
  units: string,
  fallback: number,
): number {
  if (!value) {
    return units === "userSpaceOnUse"
      ? Math.max(bounds.width, bounds.height) * fallback
      : fallback;
  }

  const trimmed = value.trim();
  if (units === "userSpaceOnUse") {
    if (trimmed.endsWith("%")) {
      return Math.max(bounds.width, bounds.height) * (Number.parseFloat(trimmed) / 100);
    }
    return readSvgLength(trimmed, fallback);
  }

  if (trimmed.endsWith("%")) {
    return Number.parseFloat(trimmed) / 100;
  }

  return readSvgLength(trimmed, fallback);
}

function parsePatternElement(
  pattern: Element,
  bounds: Rect,
  registry: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): Paint | undefined {
  const useElement = childElements(pattern).find(child => elementLocalName(child) === "use");
  let imageElement = childElements(pattern).find(child => elementLocalName(child) === "image");

  if (useElement) {
    const href = readSvgImageHref(useElement);
    if (href?.startsWith("#")) {
      imageElement = registry.getElementById(href.slice(1));
    }
  }

  return imagePaintFromPatternElement(pattern, bounds, imageElement, useElement, issues);
}

function parseFilterElement(filter: Element, issues: SvgImportIssueCollector): NodeEffect[] {
  const effects: NodeEffect[] = [];

  for (const child of childElements(filter)) {
    const localName = elementLocalName(child);
    if (localName === "fedropshadow") {
      effects.push({
        id: createId("effect"),
        type: "dropShadow",
        visible: true,
        offsetX: readSvgLength(child.getAttribute("dx")),
        offsetY: readSvgLength(child.getAttribute("dy")),
        blur: readSvgLength(child.getAttribute("stdDeviation")) * 2,
        spread: 0,
        color: parseColor(child.getAttribute("flood-color") ?? "#000000"),
      });
      continue;
    }

    if (localName === "fegaussianblur" && child.getAttribute("in") === "SourceGraphic") {
      effects.push({
        id: createId("effect"),
        type: "layerBlur",
        visible: true,
        mode: "uniform",
        blur: readSvgLength(child.getAttribute("stdDeviation")) * 2,
        start: 0,
        end: readSvgLength(child.getAttribute("stdDeviation")) * 2,
      });
      continue;
    }

    if (localName !== "fecomponenttransfer" && localName !== "fecomposite" && localName !== "femerge" && localName !== "femergenode") {
      issues.addOnce("unsupported-filter", `Filter primitive <${localName}> is not fully supported yet`, localName);
    }
  }

  return effects;
}
